import {
  Box,
  Checkbox,
  FieldSet,
  FilterChip,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Popover,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation-10';
import { RenterProfileStandalonePageCreditScore } from '@zg-rentals/ts-renter-info-client';
import { CREDIT_SCORE_CHIP_OPTIONS } from '../../constants/renterProfileFieldOptions';

type CreditScoreFieldProps = {
  value: RenterProfileStandalonePageCreditScore | null;
  onChange: (value: RenterProfileStandalonePageCreditScore | null) => void;
  /** Invoked when the user marks that they don't have a credit score yet. */
  onNoCreditScoreSelect?: () => void;
  /** Portal target for the info popover. */
  portalRoot?: HTMLElement | null;
};

export const CreditScoreField = ({ value, onChange, onNoCreditScoreSelect, portalRoot }: CreditScoreFieldProps) => {
  const isCreditScoreUnknown = value === RenterProfileStandalonePageCreditScore.UNKNOWN;

  const handleChipChange = (chipValue: RenterProfileStandalonePageCreditScore, selected: boolean) => {
    onChange(selected ? chipValue : null);
  };

  const handleClearClick = () => {
    onChange(null);
  };

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      onChange(RenterProfileStandalonePageCreditScore.UNKNOWN);
      onNoCreditScoreSelect?.();
      return;
    }

    onChange(null);
  };

  const showClearButton = value !== null && !isCreditScoreUnknown;

  const legendContent = (
    <Legend css={{ width: '100%' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
          <Paragraph textStyle="body-sm-bold" data-testid="credit-score-label">
            Your credit score
          </Paragraph>
          <Popover
            header={<Heading level={2}>How to find your credit score</Heading>}
            portalRoot={portalRoot}
            body={
              <>
                <Paragraph>
                  Many banks and credit card companies show credit scores on statements or online. You can also get a
                  free credit report from each credit bureau every year.{' '}
                </Paragraph>
                <Box css={{ marginBlockStart: 'default' }}>
                  <TextButton asChild tone="brand" textStyle="body-bold">
                    <a href="https://www.zillow.com/learn/build-credit/" target="_blank" rel="noopener noreferrer">
                      Learn more
                    </a>
                  </TextButton>
                </Box>
              </>
            }
            dividers
            shouldAwaitInteractionResponse
            tone="info"
          />
        </Box>
        {showClearButton && (
          <TextButton data-testid="credit-score-clear-button" onClick={handleClearClick}>
            Clear
          </TextButton>
        )}
      </Box>
    </Legend>
  );

  return (
    <FieldSet legend={legendContent} css={{ marginBlockEnd: '0' }}>
      <Box
        css={{ display: 'flex', gap: 'tighter', flexWrap: 'wrap' }}
        role="group"
        aria-label={isCreditScoreUnknown ? 'Credit score options (disabled)' : 'Credit score options'}
      >
        {CREDIT_SCORE_CHIP_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          return (
            <FilterChip
              key={option.label}
              data-testid={`credit-score-chip-${option.label}`}
              value={option.label}
              selected={isSelected}
              disabled={isCreditScoreUnknown}
              onSelectedChange={(selected) => handleChipChange(option.value, selected)}
            >
              {option.label}
            </FilterChip>
          );
        })}
      </Box>
      <VisuallyHidden>
        <div role="status" aria-live="polite" aria-atomic="true">
          {isCreditScoreUnknown ? 'Credit score options disabled' : ''}
        </div>
      </VisuallyHidden>
      <LabeledControl
        control={
          <Checkbox
            data-testid="credit-score-no-score-checkbox"
            checked={isCreditScoreUnknown}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
          />
        }
        label={<Label>I don&rsquo;t have a credit score yet</Label>}
      />
    </FieldSet>
  );
};
